/**
 * filterLogic — Dispatch 좌측 필터/상태 분류 순수 함수.
 *
 * RqcShipment에는 직접적인 status enum이 없어서 qty/shipped/remain 조합으로
 * 파생한다. 부동소수점 안전을 위해 epsilon 비교.
 */
import { classifyTier, type RqcShipment, type RqcTier } from "@/domain/rqc";

export type ShipmentStatus = "waiting" | "in_progress" | "done";

const EPS = 0.05;

/**
 * 완료: shipped > 0 && remain <= EPS
 * 진행중: shipped > 0 && remain > EPS
 * 대기: shipped === 0
 */
export function classifyStatus(r: RqcShipment): ShipmentStatus {
  if (r.shipped > 0 && r.remain <= EPS) return "done";
  if (r.shipped > 0) return "in_progress";
  return "waiting";
}

export interface DispatchFilter {
  status: "all" | ShipmentStatus;
  tier: "all" | RqcTier;
  /** 현장명 검색어 (대소문자 무시, 부분 매칭) */
  q?: string;
}

export function applyFilter(rows: RqcShipment[], f: DispatchFilter): RqcShipment[] {
  const q = (f.q ?? "").trim().toLowerCase();
  return rows.filter((r) => {
    if (f.status !== "all" && classifyStatus(r) !== f.status) return false;
    if (f.tier !== "all" && classifyTier(r) !== f.tier) return false;
    if (q && !r.site.toLowerCase().includes(q) && !r.company.toLowerCase().includes(q)) {
      return false;
    }
    return true;
  });
}

export interface FilterCounts {
  total: number;
  waiting: number;
  inProgress: number;
  done: number;
  gov: number;
  cust: number;
}

export function countByStatus(rows: RqcShipment[]): FilterCounts {
  const out: FilterCounts = {
    total: rows.length,
    waiting: 0,
    inProgress: 0,
    done: 0,
    gov: 0,
    cust: 0,
  };
  for (const r of rows) {
    const s = classifyStatus(r);
    if (s === "waiting") out.waiting++;
    else if (s === "in_progress") out.inProgress++;
    else out.done++;

    const t = classifyTier(r);
    if (t === "gov") out.gov++;
    else out.cust++;
  }
  return out;
}
